import React, { Fragment, useRef, useState } from "react";
import { Alert, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowBack, CloudUpload, PersonAdd } from "@mui/icons-material";
import { collection, addDoc, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import { uploadImage } from "../../utils/awsUtils";

const CreateAuthor = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [category, setCategory] = useState("Evangelist");
  const [customCategory, setCustomCategory] = useState("");
  const [description, setDescription] = useState("");
  const wordCount = description.trim() ? description.trim().split(/\s+/).length : 0;
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const fileRef = useRef();
  const navigate = useNavigate();

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setErrorMsg("Image must be under 2MB");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setErrorMsg("");
  };

  const handleCreate = async () => {
    if (!name) return setErrorMsg("Name cannot be empty");
    if (!email) return setErrorMsg("Email cannot be empty");
    if (!phone) return setErrorMsg("Phone cannot be empty");
    if (category === "Other" && !customCategory.trim()) return setErrorMsg("Please specify a category");
    if (!imageFile) return setErrorMsg("Please select a profile image");

    setLoading(true);
    try {
      const [emailMatch, phoneMatch] = await Promise.all([
        getDocs(query(collection(db, "authors"), where("email", "==", email))),
        getDocs(query(collection(db, "authors"), where("phone", "==", phone))),
      ]);
      if (!emailMatch.empty || !phoneMatch.empty) {
        setErrorMsg("This person is already an Author");
        setLoading(false);
        return;
      }

      const { imgUrl, imgId } = await uploadImage(imageFile);
      const finalCategory = category === "Other" ? customCategory.trim() : category;

      await addDoc(collection(db, "authors"), {
        name,
        email,
        phone,
        category: finalCategory,
        description: description || "",
        imgSrc: imgUrl,
        imgId,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      const userName = auth.currentUser?.email || "Admin";
      await addDoc(collection(db, "notifications"), {
        name: userName,
        message: `Created Author: ${name}`,
        createdAt: serverTimestamp(),
      });

      toast("Author created successfully!", { type: "success" });
      setTimeout(() => navigate("/author"), 2000);
    } catch (err) {
      setErrorMsg(err.message || "Failed to create author");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <div className="min-h-screen bg-[#f5f7fe] p-3 sm:p-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate("/author")}
              className="p-2 rounded-xl hover:bg-white border border-transparent hover:border-gray-100 transition"
            >
              <ArrowBack className="text-gray-400" style={{ fontSize: 20 }} />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#000C66] font-aref">Create Author</h1>
              <p className="text-gray-400 text-xs font-abel mt-0.5">Add a new article author</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-8">
            {errorMsg && <Alert severity="error" className="mb-5" onClose={() => setErrorMsg("")}>{errorMsg}</Alert>}

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">Full Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setErrorMsg(""); }}
                  placeholder="Author's full name"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrorMsg(""); }}
                  placeholder="author@example.com"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">Phone *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); setErrorMsg(""); }}
                  placeholder="+234 000 000 0000"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">Category *</label>
                <select
                  value={category}
                  onChange={(e) => { setCategory(e.target.value); setErrorMsg(""); }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition bg-white"
                >
                  <option value="Evangelist">Evangelist</option>
                  <option value="Prophet">Prophet</option>
                  <option value="Other">Other</option>
                </select>
              </Grid>

              {category === "Other" && (
                <Grid item xs={12} sm={6}>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">Specify Category *</label>
                  <input
                    type="text"
                    value={customCategory}
                    onChange={(e) => { setCustomCategory(e.target.value); setErrorMsg(""); }}
                    placeholder="e.g. Pastor, Teacher…"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider font-abel">Description</label>
                  <span className={`text-xs font-abel ${wordCount > 200 ? "text-red-500 font-semibold" : "text-gray-400"}`}>
                    {wordCount}/200 words
                  </span>
                </div>
                <textarea
                  value={description}
                  onChange={(e) => {
                    const val = e.target.value;
                    const wc = val.trim() ? val.trim().split(/\s+/).length : 0;
                    if (wc <= 200) { setDescription(val); setErrorMsg(""); }
                  }}
                  placeholder="Brief description of the author (max 200 words)"
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition resize-none"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">
                  Profile Image * <span className="text-gray-300 normal-case">(max 2MB)</span>
                </label>
                {imagePreview && (
                  <img src={imagePreview} alt="preview" className="h-24 w-24 object-cover rounded-xl border border-gray-100 mb-2" />
                )}
                <div
                  className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-[#000C66] transition"
                  onClick={() => fileRef.current.click()}
                >
                  <CloudUpload className="text-gray-300" style={{ fontSize: 24 }} />
                  <p className="text-xs text-gray-400 font-abel mt-1">
                    {imageFile ? imageFile.name : "Click to select photo"}
                  </p>
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
              </Grid>

              <Grid item xs={12} className="flex justify-end">
                <button
                  onClick={handleCreate}
                  disabled={loading}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#000C66] text-white px-8 py-3 rounded-xl font-abel font-semibold hover:bg-blue-900 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <PersonAdd style={{ fontSize: 18 }} />
                  )}
                  {loading ? "Creating..." : "Create Author"}
                </button>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default CreateAuthor;
